/**
 * G30's LINK STEP, ON `/apply` - WHAT THE PAGE SHOWS WHEN THE SERVER REFUSES TO TAKE AN APPLICATION
 * FROM AN ACCOUNT WITH NO WAY TO SIGN BACK IN (design 22 §1.4).
 *
 * THE REFUSAL. `submitApplication` and `resubmitApplication` answer 422 `UNPROCESSABLE_UNSPECIFIED`
 * when Firebase lists no sign-in method for the account - the case both operations' 422 names in
 * `authors-api.yaml`, and the guard that raises it is
 * `src/modules/authors/services/applicant-sign-in.guard.ts`. The person who meets it is an
 * anonymous reader who opened the cabinet from the phone app: the hand-off signed this browser in
 * with a CUSTOM token, so the SDK does not call them anonymous
 * (../../auth/firebase/firebase-auth-client.ts), and the gate's own G30 question,
 * `needsProviderLink`, which reads exactly that flag (../../auth/session/session-state.ts),
 * never sent them to `/sign-in`'s link step.
 *
 * SO THE STEP COMES TO THEM - THE SAME STEP, NOT A NEW ONE. The words, the two buttons and the
 * failure line are `LinkAccountPanel`'s, in the `AuthShell` card `/sign-in` draws it in, with
 * `/sign-in`'s own title (routes/(auth)/sign-in.tsx, its `ready && linkNeeded` branch), and the
 * act is the session's `linkAnonymousAccount`: the provider is added to the CURRENT account, which
 * keeps its uid (../../auth/session/link-anonymous.ts refuses a link that moves it). Never a
 * sign-out and never a second account - for a guest, either one loses everything they have filled
 * in. Sending them to `/sign-in` instead would show them "You are signed in", for the reason above.
 *
 * DONE WHEN THE ACCOUNT CHANGES. A link that took changes the identity's provider list, and the SDK
 * reports it (the id-token observer, firebase-auth-client.ts's header); the list is compared with
 * the one this step opened on, so a link that failed - one refused because that provider is
 * already on another account included - changes nothing and leaves the step up with `linkError` in
 * it. "Back to your application" is always there, so nobody is held on it. Back on the page, the
 * person sends again; nothing re-sends for them.
 *
 * IT IS MOUNTED ONLY WHEN THE REFUSAL ARRIVES (../data/provider.tsx), which is why it may call
 * `useAuthSession`: the application screens are drawn under `AuthSessionProvider` by the router,
 * and a spec that mounts them bare never meets this refusal.
 */
import { Button, Stack } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from '../../../i18n';
import { AuthShell } from '../../auth/components/AuthShell';
import { LinkAccountPanel } from '../../auth/components/LinkAccountPanel';
import type { PopupProvider } from '../../auth/firebase/auth-port';
import { useAuthSession } from '../../auth/session/AuthSessionProvider';

export interface SignInMethodStepProps {
  /** Back to the application - when a sign-in method was added, or when the person asks. */
  readonly onDone: () => void;
}

export function SignInMethodStep({ onDone }: SignInMethodStepProps): ReactNode {
  const { t } = useTranslation('application');
  const { state, linkAnonymousAccount, linkError } = useAuthSession();
  const [busyProvider, setBusyProvider] = useState<PopupProvider | null>(null);

  const providers = (state.identity?.providerLabels ?? []).join('\n');
  // What the account had when the step opened. Read on the first render only: a ref's initial
  // value is not re-read, so the comparison below is against the refusal's moment and not a moving
  // target.
  const providersWhenAsked = useRef(providers);
  useEffect(() => {
    if (providers !== '' && providers !== providersWhenAsked.current) {
      onDone();
    }
  }, [providers, onDone]);

  const link = (provider: PopupProvider): void => {
    setBusyProvider(provider);
    void linkAnonymousAccount(provider).finally(() => {
      setBusyProvider(null);
    });
  };

  return (
    <AuthShell title={t('signInMethod.title')} standfirst={t('signInMethod.standfirst')}>
      <Stack gap="md">
        <LinkAccountPanel onLink={link} busyProvider={busyProvider} error={linkError} />
        <Button variant="subtle" size="sm" onClick={onDone}>
          {t('signInMethod.back')}
        </Button>
      </Stack>
    </AuthShell>
  );
}
